import React from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Link2, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getCardShareUrl } from '../lib/cardShareUrl';

const ShareBusinessCard = ({ open, onClose, userId }) => {
  const navigate = useNavigate();
  const cardUrl = getCardShareUrl(userId);
  // QR codes are only embedded once per share — tag them with
  // src=qr so analytics can attribute scans back to physical card
  // distribution. Link sharing methods append their own src.
  const qrUrl = `${cardUrl}?src=qr_code_scan`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(qrUrl)}`;

  const handleCopyLink = async () => {
    const url = `${cardUrl}?src=copy_link`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
      onClose();
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Link copied to clipboard');
      onClose();
    }
  };

  // iter175ck — Bulletproof sms: handoff. `window.open(sms:..., '_self')`
  // intermittently misfires on mobile Safari when the page is inside
  // a dialog (the click gesture context is partially consumed by
  // Radix's dialog close-on-outside-click handler). Falling back to
  // an attached <a> element with target=_blank that we programmatically
  // click is the pattern iOS treats as a real user gesture in EVERY
  // case. We try three escalating strategies so at least one works
  // on every device.
  const launchSms = (url) => {
    // 1. Direct assignment — works on most browsers AND in a popup.
    try {
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.setAttribute('target', '_blank');
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { try { a.remove(); } catch (_) {/**/} }, 100);
    } catch (_) {/**/}
    // 2. Fallback — direct location assignment in case the anchor
    // click is suppressed by an inert iframe ancestor.
    try { window.location.href = url; } catch (_) {/**/}
    // 3. Last resort — original window.open.
    try { window.open(url, '_self'); } catch (_) {/**/}
  };

  const handleOrbText = () => {
    // iter175cl — ORB Text routes to the in-app External Text composer
    // (NOT the device's native SMS app). The message body is prefilled
    // and the user only has to pick a contact or type a phone number,
    // then tap Send. The text goes out from the user's Twilio ORB
    // number — same path as any other external text in the app.
    const url = `${cardUrl}?src=orb_text`;
    const body = `Check out my digital business card: ${url}`;
    navigate(`/chat?compose=external&prefill=${encodeURIComponent(body)}`);
    onClose();
  };

  const handleTextMessage = () => {
    // Native iOS / Android SMS handoff — opens the OS Messages app
    // with the body prefilled. User picks the recipient there.
    const url = `${cardUrl}?src=text_message`;
    const body = encodeURIComponent(`Check out my digital business card: ${url}`);
    launchSms(`sms:?body=${body}`);
    onClose();
  };

  const options = [
    { id: 'copy', label: 'Copy Link', icon: Link2, color: 'from-blue-500 to-indigo-600', action: handleCopyLink },
    { id: 'orb', label: 'ORB Text', icon: Sparkles, color: 'from-emerald-500 to-teal-600', action: handleOrbText },
    { id: 'sms', label: 'Text Message', icon: MessageSquare, color: 'from-violet-500 to-purple-600', action: handleTextMessage },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-sm p-0 rounded-t-3xl sm:rounded-2xl border-0 shadow-2xl fixed bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 translate-y-0 [&>button.absolute]:hidden"
        data-testid="share-sheet"
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-700" />
        </div>

        {/* Header — single close affordance via tap-outside / handle, no X */}
        <div className="px-5 pb-2 text-center">
          <h3 className="font-semibold text-base">Share Business Card</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Scan or pick a sharing option</p>
        </div>

        {/* QR code */}
        <div className="flex justify-center px-5 pb-4">
          <div className="p-3 bg-white rounded-2xl border border-gray-200 shadow-sm" data-testid="share-qr-wrapper">
            <img
              src={qrSrc}
              alt="Scan to open business card"
              width={180}
              height={180}
              className="block"
              data-testid="share-qr"
            />
          </div>
        </div>

        {/* Three options on ONE horizontal row */}
        <div className="px-5 pb-6 grid grid-cols-3 gap-3">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.id}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-muted/60 transition-colors active:scale-[0.97]"
                onClick={opt.action}
                data-testid={`share-${opt.id}`}
              >
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium text-[11px] text-center leading-tight">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareBusinessCard;
